/**
 * Hook genérico para persistencia en localStorage
 * Soporta modo simple y modo array con deduplicación
 */

import { useState, useEffect, useCallback } from 'react';

/**
 * @typedef {Object} UseLocalStorageOptions
 * @property {string|null} [userId=null] - ID de usuario para namespacing
 * @property {'simple'|'array'} [mode='simple'] - Modo de operación
 * @property {string} [dedupeBy='path'] - Campo para deduplicar en modo array
 * @property {number} [maxItems=10] - Máximo de items en modo array
 * @property {boolean} [addTimestamp=false] - Agregar timestamp a items en modo array
 */

// ============================================================================
// HELPERS INTERNOS (exportados para testing)
// ============================================================================

/**
 * Parsea valor de localStorage con manejo de errores
 * @param {string} key - Clave de localStorage
 * @param {*} initialValue - Valor por defecto si falla
 * @param {string|null} data - Datos crudos del localStorage
 * @returns {*} Valor parseado o initialValue
 */
export const parseLocalStorage = (key, initialValue, data) => {
  if (data === null || data === '') {
    return initialValue;
  }
  try {
    return JSON.parse(data);
  } catch (error) {
    console.error(`Error parsing localStorage key "${key}":`, error);
    return initialValue;
  }
};

/**
 * Stringifica valor para guardar en localStorage
 * @param {*} value - Valor a stringificar
 * @returns {string} Valor stringified
 */
export const stringifyValue = (value) => {
  try {
    return JSON.stringify(value);
  } catch (error) {
    console.error('Error stringifying value for localStorage:', error);
    return 'undefined';
  }
};

/**
 * Construye clave de storage con userId
 * @param {string|null} userId - ID de usuario
 * @param {string} baseKey - Clave base
 * @returns {string} Clave completa
 */
export const buildStorageKey = (userId, baseKey) => {
  if (!userId) {
    return baseKey;
  }
  return `${userId}_${baseKey}`;
};

/**
 * Agrega timestamp a un item
 * @param {Object} item - Item al que agregar timestamp
 * @returns {Object} Item con timestamp agregado
 */
export const addTimestampToItem = (item) => ({
  ...item,
  timestamp: Date.now(),
});

// ============================================================================
// HELPERS DE ARRAY (exportados para testing)
// ============================================================================

/**
 * Deduplica array por campo específico
 * @param {Array} arr - Array a deduplicar
 * @param {string} [dedupeBy] - Campo para dedup
 * @returns {Array} Array deduplicado
 */
export const deduplicateArray = (arr, dedupeBy) => {
  if (!Array.isArray(arr)) return [];
  if (!dedupeBy) {
    return [...new Set(arr.map((i) => JSON.stringify(i)))].map((i) =>
      JSON.parse(i)
    );
  }
  const seen = new Set();
  return arr.filter((item) => {
    const val = item[dedupeBy];
    if (seen.has(val)) return false;
    seen.add(val);
    return true;
  });
};

/**
 * Agrega item al array con opciones
 * @param {Array} arr - Array original
 * @param {Object} newItem - Nuevo item a agregar
 * @param {Object} options - Opciones { dedupeBy, maxItems, addTimestamp }
 * @returns {Array} Array actualizado
 */
export const appendToArray = (arr, newItem, options = {}) => {
  const { dedupeBy, maxItems, addTimestamp } = options;
  let result = arr ? [...arr] : [];
  let itemToAdd = newItem;

  if (addTimestamp) {
    itemToAdd = addTimestampToItem(newItem);
  }

  if (dedupeBy) {
    result = deduplicateArray(result, dedupeBy);
    result = result.filter((i) => i[dedupeBy] !== itemToAdd[dedupeBy]);
  }

  result.unshift(itemToAdd);

  if (maxItems && result.length > maxItems) {
    result = result.slice(0, maxItems);
  }

  return result;
};

/**
 * Remueve items de array por predicate
 * @param {Array} arr - Array original
 * @param {Function} predicate - Función que retorna true para items a remover
 * @returns {Array} Array sin los items removidos
 */
export const removeFromArray = (arr, predicate) => {
  if (!Array.isArray(arr)) return [];
  return arr.filter((item) => !predicate(item));
};

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

/**
 * Hook useLocalStorage
 * @param {string} key - Clave base para localStorage
 * @param {*} initialValue - Valor inicial
 * @param {UseLocalStorageOptions} [options={}] - Opciones
 * @returns {[*, Function]} [value, setValue]
 */
export const useLocalStorage = (key, initialValue, options = {}) => {
  const {
    userId = null,
    mode = 'simple',
    dedupeBy = 'path',
    maxItems = 10,
    addTimestamp = false,
  } = options;

  // SSR safety
  if (typeof window === 'undefined') {
    return [initialValue, () => {}];
  }

  const storageKey = buildStorageKey(userId, key);

  const readFromStorage = useCallback(() => {
    try {
      const data = localStorage.getItem(storageKey);
      return parseLocalStorage(storageKey, initialValue, data);
    } catch (error) {
      console.error(`Error reading localStorage key "${storageKey}":`, error);
      return initialValue;
    }
  }, [storageKey, initialValue]);

  const [value, setValueState] = useState(readFromStorage);

  // Sync con cambios externos (ej: otra pestaña)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === storageKey) {
        const newValue = parseLocalStorage(storageKey, initialValue, e.newValue);
        setValueState(newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [storageKey, initialValue]);

  // Update state + localStorage
  const setValue = useCallback(
    (newValue) => {
      setValueState((prev) => {
        let finalValue;

        if (mode === 'array') {
          if (typeof newValue === 'function') {
            finalValue = newValue(prev || []);
          } else {
            finalValue = newValue;
          }

          // Apply array operations if it's an object with options
          if (typeof newValue === 'object' && newValue !== null && !Array.isArray(newValue)) {
            const { append, remove, predicate, items, clear } = newValue;

            if (clear) {
              finalValue = [];
            } else if (Array.isArray(items)) {
              finalValue = items.reduce(
                (acc, item) => appendToArray(acc, item, { dedupeBy, maxItems, addTimestamp }),
                prev || []
              );
            } else if (append) {
              finalValue = appendToArray(prev || [], append, { dedupeBy, maxItems, addTimestamp });
            } else if (remove && predicate) {
              finalValue = removeFromArray(prev || [], predicate);
            }
          }
        } else {
          finalValue = typeof newValue === 'function' ? newValue(prev) : newValue;
        }

        try {
          localStorage.setItem(storageKey, stringifyValue(finalValue));
        } catch (error) {
          if (error.name === 'QuotaExceededError') {
            console.error(`localStorage quota exceeded for key "${storageKey}"`);
          } else {
            console.error(`Error writing localStorage key "${storageKey}":`, error);
          }
        }

        return finalValue;
      });
    },
    [storageKey, mode, dedupeBy, maxItems, addTimestamp]
  );

  return [value, setValue];
};

export default useLocalStorage;
